import React, {useCallback} from 'react';
import {useNavigate} from 'react-router';
import type {EditorMode} from 'editors/types';
import type {DeletePrizeFunc, Prize} from 'services/prizes/types';
import {useLocalization} from 'localization';
import {FlexBox, Scene} from 'components';
import {EditPrize} from 'editors/prize/components';
import {useSceneExtensionsContext} from 'sceneExtensions';
import {useNewOrExistingPrize, usePrizeUrlParameters} from './hooks';

export const PrizeEditSceneCore: React.FC = () => {
  const {t} = useLocalization();
  const navigate = useNavigate();
  const params = usePrizeUrlParameters();
  const mode: EditorMode = params.prizeId ? 'edit' : 'register';

  const {prize, suggestion, savePrize, deletePrize} =
    useNewOrExistingPrize(params);

  const {currentExtension} = useSceneExtensionsContext();

  const handleCancel = useCallback(() => {
    if (params.prizeId) {
      navigate(`/prize/${params.prizeId}`);
    } else {
      navigate('/prize');
    }
  }, [navigate, params.prizeId]);

  const handleSaveNew = useCallback(
    (newPrize: Prize) => {
      return savePrize(newPrize).then(savedPrize => {
        // Allow snacks to show before navigating
        setTimeout(() => {
          navigate(`/prize/${savedPrize.id}`);
        }, 500);
        return savedPrize;
      });
    },
    [navigate, savePrize],
  );

  const handleDelete = useCallback<DeletePrizeFunc>(
    prizeId => {
      return deletePrize(prizeId).then(() => {
        // Allow snacks to show before navigating
        setTimeout(() => {
          navigate('/prize');
        }, 500);
      });
    },
    [deletePrize, navigate],
  );

  return (
    <Scene
      browserTitle={t('appbar.prizes')}
      rightPanelConfig={currentExtension?.panelConfig}>
      <Scene.Content loadStatus={prize?.status} error={prize?.error?.message}>
        <FlexBox>
          {prize && prize.status === 'Loaded' ? (
            <EditPrize
              mode={mode}
              originalValue={prize.data}
              onCancel={handleCancel}
              onSave={savePrize}
              onDelete={handleDelete}
            />
          ) : (
            // Create new prize
            <EditPrize
              mode={mode}
              suggestion={suggestion}
              onCancel={handleCancel}
              onSave={handleSaveNew}
            />
          )}
        </FlexBox>
      </Scene.Content>
      {currentExtension ? (
        <Scene.RightPanel>
          {currentExtension.renderRightPanel()}
        </Scene.RightPanel>
      ) : null}
    </Scene>
  );
};
